import { WorkType } from "@faire/retailer-shared/greenhouse/departmentsRequest";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { Dropdown } from "@faire/web/slate/Input/Dropdown";
import { Column, Flex, HorizontalLine } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { createContextStore } from "@faire/web/ui/hooks/ContextStore";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import flatten from "lodash/flatten";
import uniq from "lodash/uniq";
import React from "react";
import styled from "styled-components";

import { FooterSEOContent } from "@faire/retailer/components/Footer/FooterSEOContent";
import {
  getFilteredDepartmentByLocation,
  LimitedSectionContent,
  Section,
} from "@faire/retailer/views/careers/__internal__/common/common";
import { JobOpeningsHeroSection } from "@faire/retailer/views/careers/__internal__/JobOpenings/JobOpeningsHeroSection";
import { JobOpeningsList } from "@faire/retailer/views/careers/__internal__/JobOpenings/JobOpeningsList";
import { LearnMoreSection } from "@faire/retailer/views/careers/__internal__/JobOpenings/LearnMore";

import { useDepartments } from "../common/useDepartments";

const ALL_VALUE = "ALL";
const ALL_OPTION = (strictLocalize: StrictLocalizeFunction) => ({
  label: strictLocalize({
    defaultMessage: "All",
    description: { text: "Filter value to show everything" },
  }),
  value: ALL_VALUE,
});
const EMPLOYMENT_TITLE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Employment",
    description: { text: "Title for employment work type" },
  });
const CONSULTANT_TITLE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Outside Consultant",
    description: { text: "Title for consultant work type" },
  });

const TITLE_WORK_TYPE_MAP = (strictLocalize: StrictLocalizeFunction) => ({
  [EMPLOYMENT_TITLE(strictLocalize)]: WorkType.EMPLOYMENT,
  [CONSULTANT_TITLE(strictLocalize)]: WorkType.CONSULTANT,
});

const WORK_TYPE_OPTIONS = (strictLocalize: StrictLocalizeFunction) => [
  {
    label: EMPLOYMENT_TITLE(strictLocalize),
    value: EMPLOYMENT_TITLE(strictLocalize),
  },
  {
    label: CONSULTANT_TITLE(strictLocalize),
    value: CONSULTANT_TITLE(strictLocalize),
  },
];

const { Provider: JobOpeningsProvider, useStore: useJobOpeningsStore } =
  createContextStore<{
    selectedDepartment: string;
    selectedLocation: string;
    selectedWorkType:
      | ((strictLocalize: StrictLocalizeFunction) => string)
      | string;
  }>(
    {
      selectedDepartment: ALL_VALUE,
      selectedLocation: ALL_VALUE,
      selectedWorkType: EMPLOYMENT_TITLE,
    },
    { name: "JobOpeningsStore" }
  );

const JobOpeningsHeader: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const [
    { selectedDepartment, selectedLocation, selectedWorkType },
    setJobOpeningsStore,
  ] = useJobOpeningsStore([
    "selectedDepartment",
    "selectedLocation",
    "selectedWorkType",
  ]);
  const workType: WorkType =
    TITLE_WORK_TYPE_MAP(strictLocalize)[
      typeof selectedWorkType === "string"
        ? selectedWorkType
        : selectedWorkType(strictLocalize)
    ] ?? WorkType.EMPLOYMENT;

  const { departments } = useDepartments(strictLocalize)[workType];

  const departmentOptions = [
    ALL_OPTION(strictLocalize),
    ...departments.map((department) => ({
      label: department.title,
      value: department.title,
    })),
  ];

  let filteredDepartments = [...departments];
  if (selectedDepartment !== ALL_VALUE) {
    filteredDepartments = filteredDepartments.filter(
      (department) => department.title === selectedDepartment
    );
  }

  const locationOptions = [
    ALL_OPTION(strictLocalize),
    ...uniq(
      flatten(
        filteredDepartments.map((department) => {
          if (department.childDepartments.length > 0) {
            return flatten(
              department.childDepartments.map((childDepartment) =>
                childDepartment.jobs.map((job) => job.location.name)
              )
            );
          }
          return department.jobs.map((job) => job.location.name);
        })
      )
    ).map((location) => ({
      label: location,
      value: location,
    })),
  ];

  return (
    <>
      <ResponsiveFiltersContainer>
        <FilterDropdown
          options={departmentOptions}
          value={selectedDepartment}
          onSelect={(department) =>
            setJobOpeningsStore({ selectedDepartment: department.value })
          }
          label={strictLocalize({
            defaultMessage: "Department",
            description: {
              text: "Label for department dropdown",
              img: "https://cdn.faire.com/fastly/52ef4254f71e5eeec00e30f017078866ff075584beea6bd161974c2e2c3ddbb3.png",
            },
          })}
        />
        <Spacer
          size={{
            mobileAndAbove: "1x",
            tabletAndAbove: "2x",
            desktopAndAbove: "3x",
          }}
        />
        <FilterDropdown
          options={locationOptions}
          value={selectedLocation}
          onSelect={(location) =>
            setJobOpeningsStore({ selectedLocation: location.value })
          }
          label={strictLocalize({
            defaultMessage: "Location",
            description: {
              text: "Label for location dropdown",
              img: "https://cdn.faire.com/fastly/52ef4254f71e5eeec00e30f017078866ff075584beea6bd161974c2e2c3ddbb3.png",
            },
          })}
        />
        <Spacer
          size={{
            mobileAndAbove: "1x",
            tabletAndAbove: "2x",
            desktopAndAbove: "3x",
          }}
        />
        <FilterDropdown
          options={WORK_TYPE_OPTIONS(strictLocalize)}
          value={
            typeof selectedWorkType === "string"
              ? selectedWorkType
              : selectedWorkType(strictLocalize)
          }
          onSelect={(workType) =>
            setJobOpeningsStore({ selectedWorkType: workType.value })
          }
          label={strictLocalize({
            defaultMessage: "Work Type",
            description: {
              text: "Label for work type dropdown",
              img: "https://cdn.faire.com/fastly/52ef4254f71e5eeec00e30f017078866ff075584beea6bd161974c2e2c3ddbb3.png",
            },
          })}
        />
      </ResponsiveFiltersContainer>
    </>
  );
};

const JobOpeningsBody: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const [{ selectedDepartment, selectedLocation, selectedWorkType }] =
    useJobOpeningsStore([
      "selectedDepartment",
      "selectedLocation",
      "selectedWorkType",
    ]);

  const workType: WorkType =
    (typeof selectedWorkType === "string"
      ? selectedWorkType
      : selectedWorkType(strictLocalize)) === EMPLOYMENT_TITLE(strictLocalize)
      ? WorkType.EMPLOYMENT
      : WorkType.CONSULTANT;

  const { isLoading, isError, departments } =
    useDepartments(strictLocalize)[workType];

  if (isError || isLoading) {
    return (
      <ListPlaceholder>
        {isError ? (
          <Typography>
            <StrictLocalMsg
              defaultMessage="Something went wrong. Please try again later."
              description={{
                text: "Error message to display when the available job openings at Faire fail to load",
              }}
            />
          </Typography>
        ) : (
          <LoadingSpinner />
        )}
      </ListPlaceholder>
    );
  }

  let filteredDepartments = [...departments];

  if (selectedDepartment !== ALL_VALUE) {
    filteredDepartments = filteredDepartments.filter(
      (department) => department.title === selectedDepartment
    );
  }

  if (selectedLocation !== ALL_VALUE) {
    filteredDepartments = getFilteredDepartmentByLocation(
      filteredDepartments,
      selectedLocation
    );
  }

  return <JobOpeningsList departments={filteredDepartments} />;
};

export const JobOpenings: React.FC = () => {
  return (
    <JobOpeningsProvider>
      <Column>
        <JobOpeningsHeroSection />
        <Spacer
          height={{
            mobileAndAbove: "5x",
            tabletAndAbove: "10x",
          }}
        />
        <Section $withHorizontalPadding align="center">
          <LimitedSectionContent>
            <JobOpeningsHeader />
            <Spacer height="6x" />
            <HorizontalLine />
            <Spacer height="5x" />
            <JobOpeningsBody />
          </LimitedSectionContent>
        </Section>
        <Spacer
          height={{
            mobileAndAbove: "6x",
            tabletAndAbove: "10x",
          }}
        />
        <LearnMoreSection />
        <FooterSEOContent />
      </Column>
    </JobOpeningsProvider>
  );
};

const ListPlaceholder = styled(Column).attrs({ justify: "center" })`
  align-self: center;
  min-height: 240px;
`;

const ResponsiveFiltersContainer = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;

const FilterDropdown = styled(Dropdown)`
  width: 100%;

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 320px;
  }
`;
